import type { GridInitialState } from "@mui/x-data-grid-premium";
import {
  isEqualWith,
  isObject,
  isUndefined,
  omitBy,
  type PropertyName,
} from "lodash";
import React from "react";

export interface StateView {
  label: string;
  value: GridInitialState;
  scope: "project" | "global";
}

interface ViewState {
  views: { [id: string]: StateView };
  activeViewId: string | null;
}

interface StoredViewsPayload {
  views: { [id: string]: StateView };
  activeViewId: string | null;
}

type PersistViewActions =
  | {
      type: "delete";
      userId: string;
      tableId: string;
      viewId: string;
      scope: "project" | "global";
      projectId?: string;
    }
  | {
      type: "rename";
      userId: string;
      tableId: string;
      viewId: string;
      scope: "project" | "global";
      label: string;
      projectId?: string;
    }
  | {
      type: "active";
      userId: string;
      tableId: string;
      viewId: string | null;
      projectId?: string;
    }
  | {
      type: "create";
      userId: string;
      tableId: string;
      viewId: string;
      scope: "project" | "global";
      view: StateView;
      projectId?: string;
    }
  | {
      type: "update";
      userId: string;
      tableId: string;
      viewId: string;
      scope: "project" | "global";
      value: GridInitialState;
      projectId?: string;
    };

type ViewStateActions =
  | PersistViewActions
  | {
      type: "load";
      views: { [id: string]: StateView };
      activeViewId: string | null;
    };

export const viewStateReducer: React.Reducer<ViewState, ViewStateActions> = (
  state,
  action,
) => {
  let newState;
  switch (action.type) {
    case "create": {
      newState = {
        ...state,
        activeViewId: action.viewId,
        views: {
          ...state.views,
          [action.viewId]: action.view,
        },
      };
      break;
    }

    case "delete": {
      const views = Object.fromEntries(
        Object.entries(state.views).filter(([id]) => id !== action.viewId),
      );

      let activeViewId: string | null;
      if (state.activeViewId !== action.viewId) {
        activeViewId = state.activeViewId;
      } else {
        const viewIds = Object.keys(views);

        if (viewIds.length === 0) {
          activeViewId = null;
        } else {
          activeViewId = viewIds[0];
        }
      }

      newState = {
        ...state,
        views,
        activeViewId,
      };
      break;
    }

    case "rename": {
      const target = state.views[action.viewId];
      if (!target) {
        return state;
      }

      newState = {
        ...state,
        views: {
          ...state.views,
          [action.viewId]: {
            ...target,
            label: action.label,
          },
        },
      };
      break;
    }

    case "load": {
      return {
        ...state,
        views: action.views,
        activeViewId: action.activeViewId,
      };
    }

    case "update": {
      const target = state.views[action.viewId];
      if (!target) {
        return state;
      }
      newState = {
        ...state,
        views: {
          ...state.views,
          [action.viewId]: { ...target, value: action.value },
        },
      };
      break;
    }

    case "active":
      {
        newState = {
          ...state,
          activeViewId: action.viewId,
        };
      }
      break;

    default: {
      return state;
    }
  }

  persistViewChange(action);
  return newState;
};

const STORAGE_PREFIX_V1 = "mui-datagrid:viewstore:v1";
export const REQUIRED_GLOBAL_VIEW = {
  id: "default-global-view",
  label: "Default view",
  scope: "global",
};

const getStorageKey = (
  scope: "global" | "project",
  userId: string,
  tableId: string,
  projectId?: string,
) => {
  return `${STORAGE_PREFIX_V1}:user:${userId}:${tableId}:${scope === "project" ? `project:${projectId}` : "global"}`;
};

const normalizeView = (
  view: Partial<StateView>,
  fallbackScope: "project" | "global",
): StateView | null => {
  if (!view.label || !view.value) {
    return null;
  }

  return {
    label: view.label,
    value: view.value,
    scope:
      view.scope === "global" || view.scope === "project"
        ? view.scope
        : fallbackScope,
  };
};

const readPayload = (
  key: string,
  scope: "project" | "global",
): StoredViewsPayload => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return {
        views: {},
        activeViewId: null,
      };
    }

    const parsed = JSON.parse(raw) as Partial<StoredViewsPayload>;
    const views = Object.fromEntries(
      Object.entries(parsed.views ?? {})
        .map(([id, view]) => [id, normalizeView(view, scope)])
        .filter((entry): entry is [string, StateView] => entry[1] !== null),
    );

    return {
      views,
      activeViewId:
        typeof parsed.activeViewId === "string" ? parsed.activeViewId : null,
    };
  } catch {
    return {
      views: {},
      activeViewId: null,
    };
  }
};

export function resetToDefaultView(storageKey: string) {
  const payload = readPayload(storageKey, "project");

  // if setting active and there are no custom views, skip localstorage save
  if (Object.keys(payload.views).length === 0) return;

  payload.activeViewId = REQUIRED_GLOBAL_VIEW.id;
  localStorage.setItem(storageKey, JSON.stringify(payload));
}

function persistViewChange(action: PersistViewActions) {
  // active view is only ever set on the project scope, even if it's a global view.
  // this allows for selecting a global view, but not impacting other project views
  const scope =
    action.type === "active"
      ? action.projectId
        ? "project"
        : "global"
      : action.scope;
  const storageKey = getStorageKey(
    scope,
    action.userId,
    action.tableId,
    action.projectId,
  );
  if (!storageKey) return;
  const payload = readPayload(storageKey, scope);

  // if this is a project-level table, and we're creating or deleting a GLOBAL view,
  // we need to update the active view in the PROJECT localstorage entry
  let activeStorageKey;
  let activePayload;
  if (
    action.projectId &&
    (action.type === "create" || action.type === "delete") &&
    action.scope === "global"
  ) {
    const scope = "project";
    activeStorageKey = getStorageKey(
      scope,
      action.userId,
      action.tableId,
      action.projectId,
    );
    if (!storageKey) return;
    activePayload = readPayload(activeStorageKey, scope);
  }

  switch (action.type) {
    case "delete":
      delete payload.views[action.viewId];
      if (activePayload) {
        if (activePayload.activeViewId === action.viewId) {
          activePayload.activeViewId = null;
        }
      } else {
        if (payload.activeViewId === action.viewId) {
          payload.activeViewId = null;
        }
      }
      break;
    case "rename":
      if (!payload.views[action.viewId] || !action.label) {
        return;
      }
      payload.views[action.viewId] = {
        ...payload.views[action.viewId],
        label: action.label,
      };
      break;
    case "active":
      payload.activeViewId = action.viewId;
      break;
    case "create":
      payload.views[action.viewId] = action.view;
      if (activePayload) {
        activePayload.activeViewId = action.viewId;
      } else {
        payload.activeViewId = action.viewId;
      }
      break;
    case "update":
      if (!payload.views[action.viewId] || !action.value) {
        return;
      }
      payload.views[action.viewId] = {
        ...payload.views[action.viewId],
        value: action.value,
      };
      break;
    default:
      break;
  }

  localStorage.setItem(storageKey, JSON.stringify(payload));
  if (activeStorageKey && activePayload) {
    localStorage.setItem(activeStorageKey, JSON.stringify(activePayload));
  }
}

export function loadScopedViews(
  userId: string,
  tableId: string,
  defaultGridState: GridInitialState,
  projectId?: string,
) {
  let projectPayload = undefined;
  if (projectId) {
    const projectStorageKey = getStorageKey(
      "project",
      userId,
      tableId,
      projectId,
    );
    projectPayload = readPayload(projectStorageKey, "project");
  }

  const globalStorageKey = getStorageKey("global", userId, tableId);
  const globalPayload = readPayload(globalStorageKey, "global");
  if (
    !Object.keys(globalPayload.views).includes(REQUIRED_GLOBAL_VIEW.id) &&
    defaultGridState
  ) {
    // add the default view
    globalPayload.views[REQUIRED_GLOBAL_VIEW.id] = {
      ...REQUIRED_GLOBAL_VIEW,
      value: defaultGridState,
    } as StateView;
  }

  return {
    views: {
      ...globalPayload.views,
      ...projectPayload?.views,
    },
    activeViewId:
      projectPayload?.activeViewId ??
      globalPayload?.activeViewId ??
      REQUIRED_GLOBAL_VIEW.id,
  };
}

export function equalsCustomizer(
  a: unknown,
  b: unknown,
  indexOrKey: PropertyName | undefined,
) {
  if (indexOrKey === "rowCount") {
    return true;
  }
  if (indexOrKey && isObject(a) && isObject(b)) {
    const cleanA = omitBy(a, isUndefined);
    const cleanB = omitBy(b, isUndefined);
    return isEqualWith(cleanA, cleanB, equalsCustomizer);
  }
  return undefined;
}
